










































































































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { cloneDeep, isEmpty } from 'lodash'
import { TriggeredAction } from '@/api/model/triggered-action'
import {
    ActionAssignToType,
    ActionType,
    availableActionAppointmentTypes,
    availableActionAssignToTypes,
    availableActionTypes,
    availableDelayUnits,
    availableReferralNodeTypes,
    availableReferralOrigins,
    availableReferralStatuses,
    TriggerType,
} from '@/api/types'
import { PipelineAPI, TriggeredActionAPI } from '@/api/backend-client'
import { Pipeline } from '@/api/model/pipeline'
import InputSelect from '@/components/input/InputSelect.vue'
import { User } from '@/api/model/user'
import { Branch } from '@/api/model/branch'
import { InputSelectOption } from '@/helpers/options'

@Component({
    components: { InputSelect },
})
export default class TriggeredActionForm extends Vue {
    @Prop({ required: true }) readonly value!: TriggeredAction
    @Prop({ required: true }) readonly create: boolean
    triggeredAction: TriggeredAction = null
    referralPipelines: Pipeline[] = []
    submitting: boolean = false
    loading: boolean = true
    comment: string = null


    async mounted() {
        await this.loadInitialValues()
    }

    @Watch('value')
    onValueChanged(): void {
        this.submitting = false
        this.triggeredAction = cloneDeep(this.value)

        if (this.create) {
            this.autoSelectReferralPipeline()
            this.autoSelectReferralType()
        }
    }

    async loadInitialValues() {
        await this.loadReferralPipelines()
        this.onValueChanged()
        this.loading = false
    }

    autoSelectReferralPipeline(): void {
        if (!this.$currentCompany.pipelinesEnabled) {
            return
        }

        if (!this.referralPipeline && this.availableReferralPipelines.length > 0) {
            this.referralPipeline = this.availableReferralPipelines[0].value
        }
    }

    autoSelectReferralType(): void {
        if (this.$currentCompany.pipelinesEnabled) {
            return
        }

        if (!this.triggeredAction.referralType && this.availableReferralTypes.length > 0) {
            this.triggeredAction.referralType = this.availableReferralTypes[0].value
        }
    }

    @Watch('triggeredAction.triggerType')
    onTriggerTypeChange(value): void {
        // check what the triggerType is set to
        switch (value) {
            case TriggerType.appointment:
                // if trigger type is appointment, no field is changed.
                this.triggeredAction.changedField = null
                this.triggeredAction.changedFieldValue = null
                break
            case TriggerType.change:
                // if trigger type is change, a field is changed.
                if (this.triggeredAction.actionAssignToType === ActionAssignToType.actionAssignedTo) {
                    // triggered action not based on appointment, so it is not possible to use the action assigned to users
                    this.triggeredAction.actionAssignToType = null
                }
                break
        }
    }

    @Watch('triggeredAction.changedField')
    onChangedFieldChange(newValue, oldValue): void {
        if (oldValue) {
            this.triggeredAction.changedFieldValue = null
        }
    }

    @Watch('triggeredAction.actionAssignToType')
    onActionAssignToTypeChange(value): void {
        if (value !== null) {
            this.triggeredAction.actionAssignTo = []
        }
    }

    get referralPipeline(): string|null {
        return this.triggeredAction.referralPipeline ? this.triggeredAction.referralPipeline.id : null
    }

    set referralPipeline(referralPipelineId: string|null) {
        let referralPipeline = null

        if (referralPipelineId) {
            referralPipeline = this.referralPipelines.find(rp => rp.id === referralPipelineId)
        }

        this.triggeredAction.referralPipeline = referralPipeline
    }

    get triggerType(): string {
        let triggerType = this.triggeredAction.triggerType as string

        if (triggerType === 'change') {
            triggerType += '-' + this.triggeredAction.changedField
        }

        return triggerType
    }

    set triggerType(triggerType: string) {
        let changedField = null

        if (triggerType !== TriggerType.appointment) {
            [triggerType, changedField] = triggerType.split('-')
        }

        this.triggeredAction.triggerType = triggerType as TriggerType
        this.triggeredAction.changedField = changedField
    }

    get isMeeting(): boolean {
        return this.triggeredAction.actionType === ActionType.MEETING
    }

    get isAddToPipeline(): boolean {
        return this.triggeredAction.actionType === ActionType.ADD_TO_PIPELINE
    }

    get isAssignedTo(): boolean {
        return this.triggeredAction.actionAssignToType === ActionAssignToType.assignedTo
    }

    get availablePhases(): InputSelectOption[] {
        if (!this.triggeredAction.newReferralPipeline) {
            return []
        }
        return this.triggeredAction.newReferralPipeline.activePhases.map(phase => {
            return {
                label: phase.name,
                value: phase.id,
            }
        })
    }

    async loadReferralPipelines(): Promise<void> {
        this.referralPipelines = await PipelineAPI.getAll({ company: this.$currentCompany.id })
    }

    async saveTriggeredAction(): Promise<void> {
        try {
            this.submitting = true

            if (isEmpty(this.triggeredAction.actionAssignTo)) {
                this.triggeredAction.actionAssignTo = []
            }

            if (!this.create) {
                await TriggeredActionAPI.update(this.triggeredAction.id, this.triggeredAction)
            } else if (this.create) {
                await TriggeredActionAPI.create(this.triggeredAction)
            }
            await this.$emit('save')

        } catch (error) {
            this.submitting = false
            this.$notify({
                type: 'error',
                title: 'Foutmelding',
                text: error.message,
            })
        }
    }

    get availableReferralOrigins() {
        return availableReferralOrigins().map(referralOrigin => ({
            label: this.$t('forms.triggered_action_form.origin_options.' + referralOrigin),
            value: referralOrigin,
        }))
    }

    get availablePipelines(): InputSelectOption[] {
        return this.referralPipelines.map(pipeline => {
            return {
                label: pipeline.name,
                value: pipeline.id,
            }
        })
    }

    get availableAssignedToBranches(): Branch[] {
        return this.$currentCompany.getAvailableBranches()
    }

    get availableAssignedTo(): User[] {
        return this.$currentCompany.getAvailableUsers({
            branch: this.triggeredAction.assignedToBranch,
        })
    }

    get isOwnPipeline(): boolean {
        if (!this.triggeredAction.newReferralPipeline) {
            return false
        }

        return this.triggeredAction.newReferralPipeline?.company?.id === this.$currentCompany?.id
    }

    get availableReferralPipelines() {
        return this.referralPipelines
            .map(referralPipeline => {
                return {
                    label: referralPipeline.name,
                    value: referralPipeline.id,
                }
            })
            .sort((a, b) => a.label.toString().localeCompare(b.label.toString()))
    }

    get availableNewReferralPipelines(): Pipeline[] {
        return this.referralPipelines
            .filter(pipeline => pipeline.active)
            .sort((a, b) => a.name.localeCompare(b.name))
    }

    get availableReferralTypes() {
        return availableReferralNodeTypes().map(nodeType => ({
            label: this.$t('referral.type.' + nodeType),
            value: nodeType,
        }))
    }

    get emptyFieldOptions() {
        return [
            {
                label: this.$t('forms.triggered_action_form.empty_field_options.none'),
                value: null,
            },
            {
                label: this.$t('forms.triggered_action_form.empty_field_options.appointmentDatum'),
                value: 'appointmentDatum',
            },
            {
                label: this.$t('forms.triggered_action_form.empty_field_options.leadCommission'),
                value: 'leadCommission',
            },
            {
                label: this.$t('forms.triggered_action_form.empty_field_options.action'),
                value: 'action',
            },
            {
                label: this.$t('forms.triggered_action_form.empty_field_options.transportDate'),
                value: 'transportDate',
            },
        ]
    }

    get availableTriggerTypeOptions() {
        const options = [
            {
                label: this.$t(`forms.triggered_action_form.trigger_type_options.${TriggerType.change}-status`),
                value: TriggerType.change + '-status',
            },
            {
                label: this.$t(`forms.triggered_action_form.trigger_type_options.${TriggerType.appointment}`),
                value: TriggerType.appointment,
            },
        ]

        if (this.$currentCompany.pipelinesEnabled) {
            options.unshift({
                label: this.$t(`forms.triggered_action_form.trigger_type_options.${TriggerType.change}-phase`),
                value: TriggerType.change + '-phase',
            })
        }

        return options
    }

    get availableReferralPhaseOptions() {
        const referralPipeline = this.referralPipelines.find(rp => rp.id === this.referralPipeline)
        if (!referralPipeline) {
            return []
        }

        return referralPipeline.phases.map(phase => ({
            label: phase.name,
            value: phase.id,
        }))
    }

    get availableReferralStatusOptions() {
        return availableReferralStatuses(this.$currentCompany).map(status => ({
            label: this.$t('referral.status.' + status),
            value: status,
        }))
    }

    get availableDelayUnitOptions() {
        return availableDelayUnits().map(unit => ({
            label: this.$t('forms.triggered_action_form.delay_unit.' + unit),
            value: unit,
        }))
    }

    get availableActionTypeOptions() {
        return availableActionTypes()
            .filter(value => value !== ActionType.MEETING)
            .map(actionType => ({
                label: this.$t('action.type.' + actionType),
                value: actionType,
            }))
    }

    get availableActionAppointmentTypeOptions() {
        return availableActionAppointmentTypes().map(actionAppointmentOption => ({
            label: this.$t('action.appointmentType.' + actionAppointmentOption),
            value: actionAppointmentOption,
        }))
    }

    get availableUserOptions() {
        return this.$currentCompany.getAvailableUsers()
    }

    get availableActionAssignedToTypeOptions() {
        let options = availableActionAssignToTypes().map(actionAssignToType => ({
            label: this.$t('forms.triggered_action_form.action-assigned-to-type-options.' + actionAssignToType),
            value: actionAssignToType,
        }))

        options.push({
            label: this.$t('forms.triggered_action_form.action-assigned-to-type-options.specific'),
            value: null,
        })

        if (this.triggeredAction.triggerType !== TriggerType.appointment) {
            options = options.filter(option => option.value !== ActionAssignToType.actionAssignedTo)
        }

        return options
    }

    get phase(): string {
        return this.triggeredAction?.phase?.id
    }

    set phase(id: string|null) {
        this.triggeredAction.phase = this.triggeredAction.newReferralPipeline.activePhases
            .find(pipelinePhase => pipelinePhase.id === id)
    }
}
